import { useState, useEffect } from "react";
import { UseMediaStreamResult } from "./use-media-stream-mux";

// Extend the base result type with our new flipCamera method
export interface WebcamStreamResult extends UseMediaStreamResult {
  flipCamera: () => Promise<MediaStream>;
}

export function useWebcam(): WebcamStreamResult {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');

  useEffect(() => {
    const handleStreamEnded = () => {
      setIsStreaming(false);
      setStream(null);
    };
    if (stream) {
      stream
        .getTracks()
        .forEach((track) => track.addEventListener("ended", handleStreamEnded));
      return () => {
        stream
          .getTracks()
          .forEach((track) =>
            track.removeEventListener("ended", handleStreamEnded),
          );
      };
    }
  }, [stream]);

  const start = async () => {
    const mediaStream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: facingMode
      },
    });
    setStream(mediaStream);
    setIsStreaming(true);
    return mediaStream;
  };

  const stop = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
      setIsStreaming(false);
    }
  };

  const flipCamera = async () => {
    // Stop current stream
    stop();
    // Toggle facing mode
    setFacingMode(current => current === 'user' ? 'environment' : 'user');
    // Start new stream with new facing mode
    return start();
  };

  return {
    type: "webcam",
    start,
    stop,
    flipCamera,
    isStreaming,
    stream,
  };
}